import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Eye,
  Filter,
  Search,
  X
} from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import PageHeader from '../../components/common/PageHeader';
import { adminAPI } from '../../services/api';

const domainConfig = {
  overview: {
    title: 'Platform Logs',
    eyebrow: 'Operational audit trail',
    description: 'Search platform activity and AI usage events across the system. These records are audit events, not raw API traffic.',
    subjectLabel: 'Subject',
    subjectColumn: 'Subject',
    subjectPlaceholder: 'User, company, candidate, resource, or email',
    searchPlaceholder: 'Search action, actor, subject, path, IP, resource, or metadata',
    empty: 'No platform log entries match the selected filters.'
  },
  employers: {
    title: 'Employer Logs',
    eyebrow: 'Employer audit evidence',
    description: 'Search employer account, workspace, KYC, verification, job, and request audit events with actor, IP, request, and metadata details.',
    subjectLabel: 'Company or employer',
    subjectColumn: 'Employer',
    subjectPlaceholder: 'Company name, employer name, or email',
    searchPlaceholder: 'Search company, actor, action, path, IP, resource, or metadata',
    empty: 'No employer log entries match the selected filters.'
  },
  candidates: {
    title: 'Candidate Logs',
    eyebrow: 'Candidate audit evidence',
    description: 'Search candidate account, profile, CV, AI processing, assessment, interview, and request activity with actor, IP, request, and metadata details.',
    subjectLabel: 'Candidate',
    subjectColumn: 'Candidate',
    subjectPlaceholder: 'Candidate name or email',
    searchPlaceholder: 'Search candidate, actor, action, path, IP, resource, or metadata',
    empty: 'No candidate log entries match the selected filters.'
  },
  jobs: {
    title: 'Job Logs',
    eyebrow: 'Job lifecycle evidence',
    description: 'Review job publishing, job extraction, matching context, employer ownership, AI processing, and job-related activity events.',
    subjectLabel: 'Job',
    subjectColumn: 'Job',
    subjectPlaceholder: 'Job title, employer, candidate, or email',
    searchPlaceholder: 'Search job, employer, actor, action, resource, AI metadata, or path',
    empty: 'No job log entries match the selected filters.'
  },
  interviews: {
    title: 'Interview Logs',
    eyebrow: 'Interview workflow evidence',
    description: 'Track interview state, candidate and employer context, scheduling metadata, confirmations, results, and interview-related activity.',
    subjectLabel: 'Interview',
    subjectColumn: 'Interview',
    subjectPlaceholder: 'Interview title, job, candidate, employer, or email',
    searchPlaceholder: 'Search interview, job, candidate, employer, action, status, or metadata',
    empty: 'No interview log entries match the selected filters.'
  },
  assessments: {
    title: 'Assessment Logs',
    eyebrow: 'Assessment workflow evidence',
    description: 'Review assessment definitions, candidate attempts, scoring context, warnings, review state, and related activity logs.',
    subjectLabel: 'Assessment',
    subjectColumn: 'Assessment',
    subjectPlaceholder: 'Assessment title, job, candidate, employer, or email',
    searchPlaceholder: 'Search assessment, attempt, candidate, score, actor, status, or metadata',
    empty: 'No assessment log entries match the selected filters.'
  },
  offers: {
    title: 'Offer Logs',
    eyebrow: 'Offer workflow evidence',
    description: 'Review offer state, negotiations, signing events, candidate responses, employer context, and offer lifecycle activity.',
    subjectLabel: 'Offer',
    subjectColumn: 'Offer',
    subjectPlaceholder: 'Offer title, job, candidate, employer, or email',
    searchPlaceholder: 'Search offer, negotiation, signing, candidate, employer, status, or metadata',
    empty: 'No offer log entries match the selected filters.'
  }
};

const defaultFilters = {
  search: '',
  action: '',
  actor: '',
  subject: '',
  source: '',
  resourceType: '',
  ip: '',
  method: '',
  statusCode: '',
  dateFrom: '',
  dateTo: '',
  page: 1,
  limit: 25
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const formatReference = (value) => value ? String(value).split('-')[0].toUpperCase() : 'N/A';

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
};

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const labelize = (value = '') => String(value || 'Unknown')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase())
  .replace(/\bAi\b/g, 'AI')
  .replace(/\bApi\b/g, 'API')
  .replace(/\bCv\b/g, 'CV')
  .replace(/\bHttp\b/g, 'HTTP')
  .replace(/\bIp\b/g, 'IP')
  .replace(/\bKyc\b/g, 'KYC')
  .replace(/\bRbac\b/g, 'RBAC');

const compactFilters = (filters) => Object.fromEntries(
  Object.entries(filters)
    .filter(([, value]) => value !== '' && value !== null && value !== undefined)
);

const getPrimarySubject = (log, domain) => {
  const subjectName = log?.subject?.name || log?.subject?.email;
  if (domain === 'employers') {
    return log?.employer?.companyName || subjectName || 'Employer context unavailable';
  }
  if (domain === 'candidates') {
    return log?.candidate?.name || subjectName || log?.candidate?.email || 'Candidate context unavailable';
  }
  if (domain === 'jobs') {
    return subjectName || log?.employer?.companyName || 'Job context unavailable';
  }
  if (domain === 'interviews') {
    return subjectName || log?.candidate?.name || log?.employer?.companyName || 'Interview context unavailable';
  }
  if (domain === 'assessments') {
    return subjectName || log?.candidate?.name || log?.employer?.companyName || 'Assessment context unavailable';
  }
  if (domain === 'offers') {
    return subjectName || log?.candidate?.name || log?.employer?.companyName || 'Offer context unavailable';
  }
  return subjectName || log?.employer?.companyName || log?.candidate?.name || log?.candidate?.email || 'Subject unavailable';
};

const getSubjectReference = (log, domain) => {
  if (domain === 'employers' && log?.employer?.id) return `Employer #${log.employer.id}`;
  if (domain === 'candidates' && log?.candidate?.id) return `Candidate ref ${formatReference(log.candidate.id)}`;
  const parts = [];
  if (log?.employer?.companyName && domain !== 'employers') parts.push(log.employer.companyName);
  if (log?.candidate?.name && domain !== 'candidates') parts.push(log.candidate.name);
  if (log?.candidate?.email && !parts.includes(log.candidate.email)) parts.push(log.candidate.email);
  if (log?.resource?.id) parts.push(`${labelize(log.resource.type || 'resource')} #${log.resource.id}`);
  return parts.filter(Boolean).slice(0, 2).join(' / ');
};

const joinParts = (parts) => parts.filter(Boolean).join(' / ');

const getActorLabel = (log) => joinParts([log?.actor?.name, log?.actor?.email]) || 'System or not recorded';

const getResourceLabel = (log) => joinParts([log?.resource?.type, log?.resource?.id ? `#${log.resource.id}` : '']) || 'Not recorded';

const getRequestLabel = (log) => joinParts([log?.request?.method, log?.request?.path]) || 'No request context';

const getStatusTone = (statusCode) => {
  const code = Number(statusCode);
  if (!code) return 'border-slate-200 bg-white text-slate-500';
  if (code >= 500) return 'border-rose-200 bg-rose-50 text-rose-700';
  if (code >= 400) return 'border-amber-200 bg-amber-50 text-amber-700';
  if (code >= 200 && code < 300) return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  return 'border-slate-200 bg-white text-slate-600';
};

const Badge = ({ children, className = '' }) => (
  <span className={`inline-flex items-center rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 ${className}`}>
    {children}
  </span>
);

const StatusBadge = ({ statusCode }) => {
  if (!statusCode) return <Badge>None</Badge>;
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium ${getStatusTone(statusCode)}`}>
      {statusCode}
    </span>
  );
};

const EmptyState = ({ children }) => (
  <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center text-sm leading-6 text-slate-500">
    {children}
  </div>
);

const LogsSkeleton = () => (
  <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8" aria-label="Loading logs">
    <header className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div className="space-y-3">
        <SkeletonBlock className="h-4 w-44 rounded-md" />
        <SkeletonBlock className="h-9 w-64 rounded-lg" />
        <SkeletonBlock className="h-4 w-[42rem] max-w-full rounded-md" />
      </div>
      <SkeletonBlock className="h-10 w-28 rounded-lg" />
    </header>
    <SkeletonBlock className="mb-5 h-40 rounded-[24px]" />
    <section className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => <SkeletonBlock key={index} className="h-24 rounded-lg" />)}
    </section>
    <section className="rounded-[24px] border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 p-4">
        <SkeletonBlock className="h-5 w-48 rounded-md" />
      </div>
      <div className="space-y-2 p-4">
        {Array.from({ length: 10 }).map((_, index) => <SkeletonBlock key={index} className="h-12 rounded-md" />)}
      </div>
    </section>
  </div>
);

const StatTile = ({ label, value, detail }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
    <div>
      <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{value}</p>
      {detail && <p className="mt-1 truncate text-xs font-medium text-slate-500">{detail}</p>}
    </div>
  </div>
);

const JsonBlock = ({ value }) => {
  const safeValue = value === null || value === undefined || value === '' ? {} : value;
  return (
    <pre className="max-h-80 overflow-auto rounded-[16px] border border-slate-200 bg-slate-950 p-4 text-xs leading-5 text-slate-100">
      {JSON.stringify(safeValue, null, 2)}
    </pre>
  );
};

const DetailRow = ({ label, value, mono = false }) => (
  <div className="rounded-[16px] border border-slate-100 bg-slate-50 p-4">
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
    <p className={`mt-1 break-words text-sm text-slate-900 ${mono ? 'font-mono font-normal' : 'font-medium'}`}>{value || 'Not recorded'}</p>
  </div>
);

const LogDetailModal = ({ domain, config, seedLog, detailLog, loading, error, onClose }) => {
  useEffect(() => {
    if (!seedLog) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [seedLog, onClose]);

  if (!seedLog) return null;

  const log = detailLog || seedLog;
  const subject = getPrimarySubject(log, domain);
  const transition = [log.statusTransition?.oldStatus, log.statusTransition?.newStatus].filter(Boolean).join(' -> ');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Log details"
        className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm"
      >
        <header className="flex flex-col gap-4 border-b border-slate-200 p-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge>{log.sourceLabel || labelize(log.source)}</Badge>
              <StatusBadge statusCode={log.request?.statusCode} />
              {log.statusTransition?.newStatus && <Badge>{labelize(log.statusTransition.newStatus)}</Badge>}
            </div>
            <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-900">{log.actionLabel || labelize(log.action)}</h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">{subject}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[16px] border border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
            aria-label="Close log details"
          >
            <X size={17} />
          </button>
        </header>

        <div className="max-h-[calc(92vh-112px)] overflow-y-auto p-5">
          {loading && (
            <div className="mb-4 rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              Loading full audit evidence...
            </div>
          )}
          {error && (
            <div className="mb-4 rounded-[16px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error?.message || 'Log details could not be loaded.'}
            </div>
          )}

          <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <DetailRow label="Created at" value={formatDateTime(log.createdAt)} />
            <DetailRow label="Actor" value={getActorLabel(log)} />
            <DetailRow label={config.subjectColumn || config.subjectLabel || 'Subject'} value={subject} />
            <DetailRow label="Action" value={log.action} mono />
            <DetailRow label="Resource" value={getResourceLabel(log)} mono />
            <DetailRow label="Status transition" value={transition || 'Not recorded'} mono />
            <DetailRow label="Request" value={getRequestLabel(log)} mono />
            <DetailRow label="IP address" value={log.network?.ipAddress} mono />
            <DetailRow label="User agent" value={log.network?.userAgent} />
          </section>

          <section className="mt-5 grid gap-5 xl:grid-cols-2">
            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Metadata</h3>
              <JsonBlock value={log.metadata} />
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Source details</h3>
              <JsonBlock value={log.details} />
            </div>
          </section>
        </div>
      </section>
    </div>
  );
};

const LogTableRow = ({ log, domain, onOpen }) => {
  const subject = getPrimarySubject(log, domain);
  const subjectReference = getSubjectReference(log, domain);
  const actor = getActorLabel(log);
  const request = getRequestLabel(log);
  const resource = getResourceLabel(log);

  const open = () => onOpen(log);

  return (
    <tr
      tabIndex={0}
      role="button"
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
      className="cursor-pointer outline-none transition hover:bg-slate-50 focus:bg-slate-50"
    >
      <td className="whitespace-nowrap px-6 py-4 align-top text-sm text-slate-700">{formatDateTime(log.createdAt)}</td>
      <td className="px-6 py-4 align-top">
        <p className="max-w-[260px] truncate text-sm font-medium text-slate-900">{log.actionLabel || labelize(log.action)}</p>
        <p className="mt-1 max-w-[260px] truncate font-mono text-xs text-slate-500">{log.action || 'unknown_action'}</p>
      </td>
      <td className="px-6 py-4 align-top">
        <p className="max-w-[260px] truncate text-sm font-medium text-slate-900">{subject}</p>
        <p className="mt-1 max-w-[260px] truncate text-xs text-slate-500">{subjectReference}</p>
      </td>
      <td className="px-6 py-4 align-top">
        <p className="max-w-[240px] truncate text-sm text-slate-700">{actor}</p>
        {log.actor?.role && <p className="mt-1 text-xs text-slate-500">{labelize(log.actor.role)}</p>}
      </td>
      <td className="px-6 py-4 align-top">
        <Badge>{log.sourceLabel || labelize(log.source)}</Badge>
      </td>
      <td className="px-6 py-4 align-top">
        <p className="max-w-[260px] truncate font-mono text-xs text-slate-600">{request}</p>
      </td>
      <td className="px-6 py-4 align-top">
        <p className="max-w-[220px] truncate font-mono text-xs text-slate-600">{resource}</p>
      </td>
      <td className="whitespace-nowrap px-6 py-4 align-top">
        <StatusBadge statusCode={log.request?.statusCode} />
      </td>
      <td className="whitespace-nowrap px-6 py-4 align-top text-right">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
          className="inline-flex items-center gap-2 rounded-[16px] border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-900 transition hover:bg-slate-50"
        >
          <Eye size={14} /> Details
        </button>
      </td>
    </tr>
  );
};

const PaginationBar = ({ pagination, filters, loading, onPageChange, onPageSizeChange }) => {
  const page = Number(pagination.page || filters.page || 1);
  const limit = Number(pagination.limit || filters.limit || 25);
  const total = Number(pagination.total || 0);
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const start = total ? ((page - 1) * limit) + 1 : 0;
  const end = Math.min(total, page * limit);
  const canGoBack = page > 1 && !loading;
  const canGoNext = (pagination.hasMore || page < totalPages) && !loading;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 bg-white px-4 py-3 text-sm text-slate-600 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-wrap items-center gap-3">
        <span>
          Showing <span className="font-medium text-slate-900">{formatNumber(start)}-{formatNumber(end)}</span> of <span className="font-medium text-slate-900">{formatNumber(total)}</span>
        </span>
        <label className="flex items-center gap-2">
          Rows
          <select
            value={limit}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            disabled={loading}
            className="h-9 rounded-[16px] border border-slate-200 bg-white px-2 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 disabled:opacity-60"
          >
            {PAGE_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={!canGoBack}
          className="inline-flex h-10 items-center gap-2 rounded-[16px] border border-slate-200 bg-white px-4 font-medium text-slate-900 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArrowLeft size={15} /> Previous
        </button>
        <span className="rounded-[16px] bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700">
          Page {formatNumber(page)} / {formatNumber(totalPages)}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={!canGoNext}
          className="inline-flex h-10 items-center gap-2 rounded-[16px] border border-slate-200 bg-white px-4 font-medium text-slate-900 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
};

function AdminLogsPage({ domain }) {
  const config = domainConfig[domain] || domainConfig.employers;
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [filters, setFilters] = useState(defaultFilters);
  const [selectedLog, setSelectedLog] = useState(null);

  const logsQuery = useQuery(
    ['admin-logs', domain, filters],
    () => adminAPI.getAdminLogs(domain, compactFilters(filters)),
    {
      keepPreviousData: true,
      staleTime: 30000,
      refetchOnWindowFocus: false
    }
  );

  const detailQuery = useQuery(
    ['admin-log-detail', domain, selectedLog?.source, selectedLog?.logId],
    () => adminAPI.getAdminLogDetail(domain, selectedLog.source, selectedLog.logId),
    {
      enabled: Boolean(selectedLog?.source && selectedLog?.logId),
      staleTime: 60000,
      refetchOnWindowFocus: false
    }
  );

  const data = logsQuery.data || {};
  const logs = useMemo(() => data.logs || [], [data.logs]);
  const summary = data.summary || {};
  const pagination = data.pagination || { page: filters.page, limit: filters.limit, total: 0, hasMore: false };
  const availableSources = data.availableSources || [];
  const warnings = data.warnings || [];
  const activeDetail = detailQuery.data?.log || selectedLog;
  const sourceCounts = summary.sources || [];

  useEffect(() => {
    if (!selectedLog) return;
    if (!logs.some((log) => log.id === selectedLog.id)) setSelectedLog(null);
  }, [logs, selectedLog]);

  const activeFilterCount = useMemo(() => (
    Object.entries(filters).filter(([key, value]) => !['page', 'limit'].includes(key) && value).length
  ), [filters]);

  const activeSourceCount = sourceCounts.filter((source) => source.count > 0).length;

  const applyFilters = (event) => {
    event.preventDefault();
    setSelectedLog(null);
    setFilters({ ...draftFilters, page: 1 });
  };

  const resetFilters = () => {
    setSelectedLog(null);
    setDraftFilters(defaultFilters);
    setFilters(defaultFilters);
  };

  const setPage = (page) => {
    const next = { ...filters, page: Math.max(1, page) };
    setSelectedLog(null);
    setFilters(next);
    setDraftFilters((current) => ({ ...current, page: next.page }));
  };

  const setPageSize = (limit) => {
    setSelectedLog(null);
    setFilters((current) => ({ ...current, limit, page: 1 }));
    setDraftFilters((current) => ({ ...current, limit, page: 1 }));
  };

  const updateDraft = (key, value) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const setSourceFilter = (sourceKey) => {
    const nextSource = filters.source === sourceKey ? '' : sourceKey;
    setSelectedLog(null);
    setDraftFilters((current) => ({ ...current, source: nextSource }));
    setFilters((current) => ({ ...current, source: nextSource, page: 1 }));
  };

  if (logsQuery.isLoading && !logsQuery.data) {
    return <LogsSkeleton />;
  }

  return (
    <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <PageHeader
        eyebrow={config.eyebrow}
        title={config.title}
        description={config.description}
      />

      <section className="mb-5 rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Search and filters</h2>
            <p className="mt-1 text-xs leading-5 text-slate-500">Queries are paged on the server. Keep filters specific for high-volume investigations.</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Filter size={15} className="text-slate-900" />
            {activeFilterCount ? `${activeFilterCount} active filter${activeFilterCount === 1 ? '' : 's'}` : 'No active filters'}
          </div>
        </div>

        <form onSubmit={applyFilters} className="space-y-3">
          <div className="grid gap-3 xl:grid-cols-[minmax(280px,1.5fr)_minmax(180px,0.75fr)_minmax(160px,0.55fr)]">
            <label className="relative">
              <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-900" />
              <input
                value={draftFilters.search}
                onChange={(event) => updateDraft('search', event.target.value)}
                placeholder={config.searchPlaceholder}
                className="h-10 w-full rounded-[16px] border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
              />
            </label>
            <input
              value={draftFilters.subject}
              onChange={(event) => updateDraft('subject', event.target.value)}
              placeholder={config.subjectPlaceholder}
              aria-label={config.subjectLabel}
              className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            />
            <select
              value={draftFilters.source}
              onChange={(event) => updateDraft('source', event.target.value)}
              className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            >
              <option value="">All sources</option>
              {availableSources.map((source) => <option key={source.key} value={source.key}>{source.label}</option>)}
            </select>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-6">
            <input
              value={draftFilters.action}
              onChange={(event) => updateDraft('action', event.target.value)}
              placeholder="Action"
              className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            />
            <input
              value={draftFilters.actor}
              onChange={(event) => updateDraft('actor', event.target.value)}
              placeholder="Actor name or email"
              className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            />
            <input
              value={draftFilters.resourceType}
              onChange={(event) => updateDraft('resourceType', event.target.value)}
              placeholder="Resource type"
              className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            />
            <input
              value={draftFilters.ip}
              onChange={(event) => updateDraft('ip', event.target.value)}
              placeholder="IP address"
              className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            />
            <select
              value={draftFilters.method}
              onChange={(event) => updateDraft('method', event.target.value)}
              className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            >
              <option value="">Any method</option>
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => <option key={method} value={method}>{method}</option>)}
            </select>
            <input
              value={draftFilters.statusCode}
              onChange={(event) => updateDraft('statusCode', event.target.value.replace(/\D/g, '').slice(0, 3))}
              placeholder="Status"
              inputMode="numeric"
              className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            />
          </div>

          <div className="grid gap-3 lg:grid-cols-[180px_180px_minmax(0,1fr)_auto_auto]">
            <input
              type="date"
              value={draftFilters.dateFrom}
              onChange={(event) => updateDraft('dateFrom', event.target.value)}
              className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            />
            <input
              type="date"
          value={draftFilters.dateTo}
              onChange={(event) => updateDraft('dateTo', event.target.value)}
              className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
            />
            <div className="hidden lg:block" />
            <button type="button" onClick={resetFilters} className="h-10 rounded-[16px] border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 transition hover:bg-slate-50">
              Reset
            </button>
            <button type="submit" className="h-10 rounded-[16px] bg-slate-950 px-5 text-sm font-semibold text-white transition hover:bg-slate-800">
              Apply filters
            </button>
          </div>
        </form>
      </section>

      {logsQuery.isError && (
        <div className="mb-5 rounded-[24px] border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-700" />
            <p className="font-semibold text-rose-900">{logsQuery.error?.message || 'Logs could not be loaded.'}</p>
          </div>
        </div>
      )}

      {!!warnings.length && (
        <div className="mb-5 rounded-[24px] border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div>
              <p className="font-semibold text-amber-900">Some log sources could not be loaded.</p>
              <p className="mt-1 font-medium leading-5 text-amber-800">
                {warnings.map((warning) => warning.message).join(' ')} Other available sources are still shown below.
              </p>
            </div>
          </div>
        </div>
      )}

      <section className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Matched records" value={formatNumber(summary.total)} detail="Current query result count" />
        <StatTile label="Visible rows" value={formatNumber(logs.length)} detail={`Page ${pagination.page || filters.page} at ${pagination.limit || filters.limit} rows`} />
        <StatTile label="Active sources" value={formatNumber(activeSourceCount)} detail={`${formatNumber(sourceCounts.length)} sources available`} />
        <StatTile label="Window" value={filters.dateFrom || filters.dateTo ? 'Filtered' : 'All time'} detail={filters.dateFrom || filters.dateTo ? `${filters.dateFrom || 'Start'} to ${filters.dateTo || 'now'}` : 'No date filter'} />
      </section>

      {!!sourceCounts.length && (
        <section className="mb-5 flex flex-wrap gap-2">
          {sourceCounts.map((source) => (
            <button
              key={source.key}
              type="button"
              onClick={() => setSourceFilter(source.key)}
              className={`rounded-[16px] border px-4 py-2 text-xs font-medium transition ${filters.source === source.key ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
            >
              {source.label}: {formatNumber(source.count)}
            </button>
          ))}
        </section>
      )}

      <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-slate-100 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Audit entries</h2>
            <p className="mt-1 text-xs leading-5 text-slate-500">Select a row to open full evidence, request metadata, and source details.</p>
          </div>
          {logsQuery.isFetching && logs.length > 0 && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
              Updating
            </span>
          )}
        </div>

        {logs.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-[1240px] divide-y divide-slate-100 text-left">
              <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4">Time</th>
                  <th className="px-6 py-4">Event</th>
                  <th className="px-6 py-4">{config.subjectColumn || config.subjectLabel || 'Subject'}</th>
                  <th className="px-6 py-4">Actor</th>
                  <th className="px-6 py-4">Source</th>
                  <th className="px-6 py-4">Request</th>
                  <th className="px-6 py-4">Resource</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Open</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {logs.map((log) => (
                  <LogTableRow
                    key={log.id}
                    log={log}
                    domain={domain}
                    onOpen={setSelectedLog}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyState>{config.empty}</EmptyState>
          </div>
        )}

        <PaginationBar
          pagination={pagination}
          filters={filters}
          loading={logsQuery.isFetching}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </section>

      <LogDetailModal
        domain={domain}
        config={config}
        seedLog={selectedLog}
        detailLog={activeDetail}
        loading={detailQuery.isFetching && Boolean(selectedLog)}
        error={detailQuery.error}
        onClose={() => setSelectedLog(null)}
      />
    </div>
  );
}

export default AdminLogsPage;
